import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { NunaBasic, NunaBusiness, NunaPersonal } from 'assets'
import { ServiceCard } from 'components'
import { AnimatePresence, motion } from 'framer-motion'
import { useDataLanding } from 'hooks'
import { useState } from 'react'
import {
  cardsContainer,
  cardsDesktop,
  carouselContainer,
  carouselIndicators,
  carouselMobile,
  carouselSlide,
  carouselSlideContent,
  containerVariants,
  indicator,
  indicatorContainer,
  plansContainer,
  plansContent,
  plansTextContainer,
  titleTextStyle,
} from 'styles'

const tabVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
    scale: 0.95,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 300 : -300,
    opacity: 0,
    scale: 0.95,
  }),
}

export const PlansSection = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { content } = useDataLanding()
  const [currentCard, setCurrentCard] = useState(0)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [direction, setDirection] = useState(0) // 1 para derecha, -1 para izquierda
  const defaultLink = process.env.REACT_APP_MARKETPLACE_NUNA_PROCESS!
  const whatsappLink = process.env.REACT_APP_WHATSAPP_LINK!

  const plans = [
    {
      title: 'Nuna Básico',
      description: [
        '50 estampas',
        'Soporte 8/5',
        'Firma Pdf',
        'Gestión de certificados',
        'Validador de firma Pdf',
      ],
      image: NunaBasic,
      buttonText: 'CONTRATAR SERVICIO',
      buttonTextSecond: 'MÁS INFORMACIÓN',
      buttonLink: content?.plans?.basicPlanButtonLink || defaultLink,
    },
    {
      title: 'Nuna Personal',
      description: [
        '100 estampas',
        'Soporte 8/5',
        'Firma Pdf',
        'Gestión de certificados',
        'Validador de firma Pdf',
        'Firma colaborativa',
      ],
      image: NunaPersonal,
      buttonText: 'CONTRATAR SERVICIO',
      buttonTextSecond: 'MÁS INFORMACIÓN',
      buttonLink: content?.plans?.personalPlanButtonLink || defaultLink,
    },
    {
      title: 'Nuna Profesional',
      description: [
        '500 estampas',
        'Soporte 8/5',
        'Firma Pdf',
        'Gestión de certificados',
        'Validador de firma Pdf',
        'Firma colaborativa',
      ],
      image: NunaBusiness,
      buttonText: 'CONTRATAR SERVICIO',
      buttonTextSecond: 'MÁS INFORMACIÓN',
      buttonLink: content?.plans?.corporatePlanButtonLink || defaultLink,
    },
    {
      title: 'Nuna Corporativo',
      description: [
        'Personaliza con Nunakey con todas las funcionalidades que tu empresa necesite, integra tus procesos de firmado en una sola plataforma, agenda tu asesoría gratuita con nosotros.',
      ],
      image: NunaBusiness,
      buttonText: 'CONTRATAR SERVICIO',
      buttonTextSecond: 'MÁS INFORMACIÓN',
      buttonLink: whatsappLink,
    },
  ]

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(0)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (isLeftSwipe && currentCard < plans.length - 1) {
      setDirection(1)
      setCurrentCard(currentCard + 1)
    }
    if (isRightSwipe && currentCard > 0) {
      setDirection(-1)
      setCurrentCard(currentCard - 1)
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    setTouchStart(e.clientX)
    setTouchEnd(0)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return
    setTouchEnd(e.clientX)
  }

  const handleMouseUp = () => {
    if (!isDragging) return
    setIsDragging(false)

    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (isLeftSwipe && currentCard < plans.length - 1) {
      setDirection(1)
      setCurrentCard(currentCard + 1)
    }
    if (isRightSwipe && currentCard > 0) {
      setDirection(-1)
      setCurrentCard(currentCard - 1)
    }
  }

  const handleIndicatorClick = (index: number) => {
    if (index > currentCard) {
      setDirection(1)
    } else if (index < currentCard) {
      setDirection(-1)
    }
    setCurrentCard(index)
  }

  return (
    <Box id="plans" sx={plansContainer}>
      <Box
        sx={plansContent}
        component={motion.div}
        initial="offscreen"
        whileInView="onscreen"
        variants={containerVariants}
        viewport={{ once: true }}
      >
        <Box sx={plansTextContainer}>
          <Typography
            variant="h3"
            sx={{
              ...titleTextStyle,
              fontWeight: 600,
              mb: 2,
              textAlign: 'center',
              color: '#639006ff',
            }}
          >
            {content?.plans?.title}
          </Typography>
        </Box>

        <Box sx={cardsContainer} id="plans-cards">
          <Box sx={carouselContainer}>
            {isMobile ? (
              <Box
                sx={{
                  ...carouselMobile,
                  cursor: isDragging ? 'grabbing' : 'grab',
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <Box sx={carouselSlide}>
                  <Box sx={carouselSlideContent}>
                    <AnimatePresence mode="wait" custom={direction}>
                      <motion.div
                        key={currentCard}
                        custom={direction}
                        variants={tabVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{
                          duration: 0.4,
                          ease: 'easeInOut',
                        }}
                        style={{ width: '100%' }}
                      >
                        <ServiceCard
                          title={plans[currentCard].title}
                          description={plans[currentCard].description}
                          image={plans[currentCard].image}
                          buttonText={plans[currentCard].buttonText}
                          buttonTextAlt={plans[currentCard].buttonTextSecond}
                          onClick={() => {
                            window.open(plans[currentCard].buttonLink, '_blank')
                          }}
                          onClickAlt={() => {
                            window.open(whatsappLink, '_blank')
                          }}
                        />
                      </motion.div>
                    </AnimatePresence>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={cardsDesktop}>
                {plans.map((plan, index) => (
                  <Box key={index}>
                    <ServiceCard
                      title={plan.title}
                      description={plan.description}
                      image={plan.image}
                      buttonText={plan.buttonText}
                      buttonTextAlt={plan.buttonTextSecond}
                      onClick={() => {
                        window.open(plan.buttonLink, '_blank')
                      }}
                      onClickAlt={() => {
                        window.open(whatsappLink, '_blank')
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {isMobile && (
            <Box sx={carouselIndicators}>
              <Box sx={indicatorContainer}>
                {plans.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => handleIndicatorClick(index)}
                    component={motion.div}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                    animate={{
                      backgroundColor:
                        currentCard === index
                          ? 'rgba(255, 255, 255, 1)'
                          : 'rgba(255, 255, 255, 0.4)',
                      scale: currentCard === index ? 1.1 : 1,
                    }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    sx={{
                      ...indicator,
                      '&:hover': {
                        backgroundColor:
                          currentCard === index
                            ? 'rgba(255, 255, 255, 1)'
                            : 'rgba(255, 255, 255, 0.6)',
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
